/**
 * Measurements Service
 * 
 * Service for fetching measurement data with caching mechanisms
 * for improved performance.
 */

import { apiClient } from '../api';
import { 
  TechnologyType, 
  TrendChartData,
  DateRange
} from './types';
import { trendChartDataList } from './equipment-mock-data';

// Cache configuration
const CACHE_TTL = 60 * 1000; // 1 minute in milliseconds

// Cache structure
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// In-memory cache
const measurementsCache: Record<string, CacheEntry<any>> = {};

/**
 * Generates a cache key for measurements
 */
const getCacheKey = (
  site: string,
  asset: string,
  position: string,
  technology: TechnologyType,
  dateRange?: DateRange
): string => {
  const dateRangeKey = dateRange ? `${dateRange.startDate}_${dateRange.endDate}` : 'all';
  return `measurements_${site}_${asset}_${position}_${technology}_${dateRangeKey}`;
};

/**
 * Checks if a cache entry is still valid
 */
const isCacheValid = (entry: CacheEntry<any>): boolean => {
  return Date.now() - entry.timestamp < CACHE_TTL;
};

/**
 * Measurements Service with methods for fetching measurement data
 */
export const measurementsService = {
  /**
   * Fetches trend chart data for a specific equipment ID and technology
   * with caching for improved performance
   * @param site - Site identifier
   * @param asset - Asset identifier
   * @param position - Position identifier (may include equipment prefix)
   * @param technology - Technology type for measurements
   * @param dateRange - Optional specific date range
   * @returns Promise resolving to trend chart data
   */
  async getTrendChartData(
    site: string,
    asset: string,
    position: string,
    technology: TechnologyType,
    dateRange?: DateRange
  ): Promise<TrendChartData> {

    if (technology === 'thermal') {
      return trendChartDataList['thermal'];
    }

    // The position comes with the equipment id as prefix.
    // This is done to avoid conflicts when the tree renders several equipments with the same position names
    // so here we need to extract the position
    if (position.includes("#")) {
      [asset, position] = position.split("#");
    }

    const cacheKey = getCacheKey(site, asset, position, technology, dateRange);
    
    // Check cache first
    const cachedData = measurementsCache[cacheKey];
    if (cachedData && isCacheValid(cachedData)) {
      return cachedData.data;
    }
    
    // Fetch from API if not in cache or cache expired
    const params: Record<string, string> = {
      site,
      asset: encodeURIComponent(asset),
      position: encodeURIComponent(position),
      tech: technology,
    };
    
    if (dateRange) {
      params.start_date = dateRange.startDate;
      params.end_date = dateRange.endDate;
    }
    
    let data = await apiClient.get<TrendChartData>(
      '/measurements',
      { params }
    );

    // Update cache
    measurementsCache[cacheKey] = {
      data,
      timestamp: Date.now()
    };
    
    return data;
  },

  /**
   * Clears the measurements cache for a specific equipment ID and technology
   * or the entire cache if no parameters are provided
   * @param site - Optional site identifier to filter cache entries
   * @param asset - Optional asset identifier to filter cache entries
   * @param position - Optional position identifier to filter cache entries
   * @param technology - Optional technology type to filter cache entries
   */
  clearCache(site?: string, asset?: string, position?: string, technology?: TechnologyType): void {
    if (site && asset && position && technology) {
      Object.keys(measurementsCache).forEach(key => {
        if (key.startsWith(`measurements_${site}_${asset}_${position}_${technology}`)) {
          delete measurementsCache[key];
        }
      });
    } else if (site && asset && position) {
      Object.keys(measurementsCache).forEach(key => {
        if (key.startsWith(`measurements_${site}_${asset}_${position}`)) {
          delete measurementsCache[key];
        }
      });
    }  else if (site && asset) {
      Object.keys(measurementsCache).forEach(key => {
        if (key.startsWith(`measurements_${site}_${asset}`)) {
          delete measurementsCache[key];
        }
      });
    } else if (site) {
      Object.keys(measurementsCache).forEach(key => {
        if (key.startsWith(`measurements_${site}`)) {
          delete measurementsCache[key];
        }
      });
    } else {
      // Clear all cache
      Object.keys(measurementsCache).forEach(key => {
        delete measurementsCache[key];
      });
    }
  }
};